import React, { useCallback, useState } from 'react';
import '../css/problem-input.css';
import '../css/problem.css';
import { SubmitBar } from '../components/submit-bar';
import { APP_CFG } from '../appcfg';
import {
  showConfetti,
  submitResponse,
  moveOnToNextPage,
  setStage
} from '../redux/actions/ui';

import {
  Panel,
  setSideBarCorrect,
  setSideBarOpenPanel,
  setSideBarSecondTry,
  setSideBarIncorrect
} from '../sidebar-state';
import { getSecondTryHint, getReviewMessage, getCorrectElement, getGeneralHint } from '../components/side-bar';
import { InputContent } from '../page-content/input-content';
import {
  ProblemOption,
  ProblemStage,
  HintsDefinition,
  InputData,
  InputComp,
  ITSEventType
} from 'its-pages';
import ReactMarkdown from 'react-markdown';
import { useAppDispatch, useAppSelector } from '../redux/hooks';

interface Props {
  inputContent: InputContent;
}

export const ProblemInput = (props: Props) => {
  const [firstTryScore, setFirstTryScore] = useState(0);
  const [disableSubmit, setDisableSubmit] = useState(true);
  const { inputContent: { pageData } } = props;
  const isMultiChoice = pageData.problemType === 'multiChoice';

  const correctAnswerStreak = useAppSelector(state => state.progression.correctAnswerStreak);
  const stage = useAppSelector(state => state.ui.stage);
  const dispatch = useAppDispatch();

  const inputData = pageData as InputData;
  const { options, hints, problemQuality } = inputData;

  const problemScore = useCallback(() => {
    if (!options.length) return 0;

    const countCorrect = options.reduce(
      (count, v) => count + (v.isSelected === v.isCorrectResponse ? 1 : 0),
      0
    );

    return countCorrect / options.length;
  }, [options]);

  const isProblemCorrect = (): boolean => {
    return problemScore() === 1;
  };

  const isReview = stage === ProblemStage.ShowResult;
  const secondTry = stage === ProblemStage.SecondTry;

  const getReviewText = (): JSX.Element => {
    return (
      <div>
        {getReviewMessage(correctAnswerStreak, problemScore() > firstTryScore)}
        {getGeneralHint(hints, problemQuality)}
      </div>
    );
  };

  const resetOptions = (): void => {
    if (APP_CFG.autoResetButtons) {
      options.forEach((options: ProblemOption): void => {
        options.isSelected = false;
      });
    }
  };

  const onSubmit = () => {
    if (stage === ProblemStage.ShowResult) {
      dispatch(moveOnToNextPage());
      setDisableSubmit(true);
    } else if (isProblemCorrect()) {
      dispatch(showConfetti(true));
      setSideBarCorrect(getCorrectElement(inputData.seed));
      dispatch(setStage(ProblemStage.ShowResult));
      dispatch(submitResponse());
    } else if (stage === ProblemStage.CollectInput) {
      dispatch(setStage(ProblemStage.SecondTry));
      setFirstTryScore(problemScore);
      setDisableSubmit(true);
      setSideBarSecondTry(getSecondTryHint(isMultiChoice, hints, problemQuality));
      inputData.recordEvent(ITSEventType.firstTryWrong);
      resetOptions();
      // forceUpdate();
    } else if (stage === ProblemStage.SecondTry) {
      dispatch(setStage(ProblemStage.ShowResult));
      setSideBarIncorrect(getReviewText());
      dispatch(submitResponse());
    } else {
      dispatch(moveOnToNextPage());
    }
  };

  const onReviewed = () => {
    if (isReview) {
      setSideBarOpenPanel(Panel.None);
    }
  };

  const onChange = () => {
    setDisableSubmit(false);
  };

  return (
    <div className='Problem ProblemInput' style={{ padding: '0.5rem'}}>
      <InputComp
        stage={stage}
        pageData={inputData}
        onReviewed={onReviewed}
        onChange={onChange}
      />
      <SubmitBar
        onSubmit={onSubmit}
        buttonText={secondTry ? 'Try Again' : isReview ? 'Continue' : 'Submit'}
        buttonClass={'SubmitBarButton'}
        disableSubmit={disableSubmit}
        disabledTip={
          isReview
            ? undefined
            : secondTry
            ? 'Change your selection'
            : 'Make a selection'
        }
      />
    </div>
  );
};
